import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircleOutline, ErrorOutline } from "@mui/icons-material";
import p4lLogo from "../assets/logo/PfL Logo no words 2023-11-21-01.png";
import topicsBackground from "../assets/images/topics_background.svg";
import Button from "../componentLibrary/Button";
import { useGetTutorialStatus } from "../api/queries";
import { useAuth } from "../contexts/AuthContext";

type VerificationState =
  | "initial"
  | "loading"
  | "success"
  | "already_confirmed"
  | "invalid"
  | "expired"
  | "not_found"
  | "limit_exceeded"
  | "missing_parameters"
  | "unknown_error";

const ConfirmUserPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [verificationState, setVerificationState] =
    useState<VerificationState>("initial");
  const [errorMessage, setErrorMessage] = useState("");

  const clientId = searchParams.get("client_id");
  const userName = searchParams.get("user_name");
  const confirmationCode = searchParams.get("confirmation_code");

  // Check if all required parameters are present
  const hasRequiredParams = clientId && userName && confirmationCode;

  const { isAuthenticated: isAuth } = useAuth();
  const shouldCheckTutorial =
    (verificationState === "success" ||
      verificationState === "already_confirmed") &&
    isAuth;
  const { data: tutorialStatus, isLoading: tutorialLoading } =
    useGetTutorialStatus(shouldCheckTutorial);

  useEffect(() => {
    if (!hasRequiredParams) {
      setVerificationState("missing_parameters");
      setErrorMessage(
        "Invalid confirmation link. Please check your email and try again.",
      );
    }
  }, [hasRequiredParams]);

  useEffect(() => {
    if (shouldCheckTutorial && !tutorialLoading && tutorialStatus) {
      if (tutorialStatus.needsTutorial) {
        navigate("/tutorial");
      }
    }
  }, [shouldCheckTutorial, tutorialLoading, tutorialStatus, navigate]);

  const handleConfirmEmail = async () => {
    if (!hasRequiredParams) {
      setVerificationState("missing_parameters");
      setErrorMessage(
        "Missing required parameters. Please use the link from your email.",
      );
      return;
    }

    setVerificationState("loading");
    setErrorMessage("");

    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
      const response = await fetch(
        `${apiBaseUrl}/verify-email?client_id=${clientId}&user_name=${userName}&confirmation_code=${confirmationCode}`,
        {
          method: "GET",
        },
      );

      const data = await response.json();

      if (response.ok || data.success) {
        if (data.reason === "already_confirmed") {
          setVerificationState("already_confirmed");
        } else {
          setVerificationState("success");
        }
      } else {
        const reason = data.reason || "unknown_error";
        setVerificationState(reason as VerificationState);
        setErrorMessage(
          data.message ||
            "Failed to verify your email. The link may have expired or already been used.",
        );
      }
    } catch (err) {
      setVerificationState("unknown_error");
      setErrorMessage(
        "An error occurred while verifying your email. Please try again or contact support.",
      );
    }
  };

  const getTitle = () => {
    switch (verificationState) {
      case "initial":
      case "loading":
        return "Verify Your Email Address to Activate Your Account";
      case "success":
        return "Email Verified!";
      case "already_confirmed":
        return "Email Already Verified";
      case "invalid":
        return "Invalid Verification Code";
      case "expired":
        return "Verification Link Used or Expired";
      case "not_found":
        return "User Not Found";
      case "limit_exceeded":
        return "Too Many Attempts";
      case "missing_parameters":
        return "Invalid Verification Link";
      default:
        return "Verification Failed";
    }
  };

  const getMessage = () => {
    switch (verificationState) {
      case "initial":
        return "Click the button below to confirm your email and complete your registration.";
      case "loading":
        return "Verifying your email address...";
      case "success":
        return "Your email has been successfully verified! You can now sign in to your account and access all features.";
      case "already_confirmed":
        return "Good news! Your email address has already been verified. You can proceed to sign in to your account.";
      case "invalid":
        return (
          errorMessage ||
          "The verification code is invalid. Please check your email for the correct link or request a new verification email."
        );
      case "expired":
        return (
          errorMessage ||
          "This verification link has already been used or has expired. If you haven't verified your email yet, please request a new verification link. Otherwise, you can sign in to your account."
        );
      case "not_found":
        return (
          errorMessage ||
          "We couldn't find an account with this information. Please check your details or create a new account."
        );
      case "limit_exceeded":
        return (
          errorMessage ||
          "Too many verification attempts have been made. Please wait a few minutes before trying again."
        );
      case "missing_parameters":
        return (
          errorMessage ||
          "The verification link is incomplete or invalid. Please check your email and click the complete link, or request a new verification email."
        );
      default:
        return (
          errorMessage ||
          "We couldn't verify your email address. Please try again or contact support for assistance."
        );
    }
  };

  const getIcon = () => {
    switch (verificationState) {
      case "success":
        return (
          <CheckCircleOutline
            className="text-green-500"
            style={{ fontSize: "64px" }}
          />
        );
      case "already_confirmed":
        return (
          <CheckCircleOutline
            className="text-blue-500"
            style={{ fontSize: "64px" }}
          />
        );
      case "limit_exceeded":
        return (
          <ErrorOutline
            className="text-orange-500"
            style={{ fontSize: "64px" }}
          />
        );
      default:
        return (
          <ErrorOutline className="text-red-500" style={{ fontSize: "64px" }} />
        );
    }
  };

  const isSuccessState =
    verificationState === "success" ||
    verificationState === "already_confirmed";
  const isErrorState =
    verificationState !== "initial" &&
    verificationState !== "loading" &&
    !isSuccessState;

  const handleGoToLogin = () => {
    navigate("/login");
  };

  const handleGoToSignup = () => {
    navigate("/signup");
  };

  const handleGoToHome = () => {
    navigate("/");
  };

  const handleGoToTutorial = () => {
    navigate("/tutorial");
  };

  return (
    <div className="min-h-screen relative pb-6 bg-gradient-to-b from-electrum-100 via-white to-white">
      {/* Fixed Background Image */}
      <div
        className="fixed inset-0 z-0 top-8"
        style={{
          backgroundImage: `url(${topicsBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center top",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
        }}
      />

      <div className="relative z-10">
        <div className="flex flex-col mx-auto w-full max-w-md px-4 pt-16 space-y-6">
          <div className="bg-white/90 backdrop-blur-sm rounded-lg shadow-lg p-8">
            {/* Logo and Title */}
            <div className="flex flex-col items-center mb-8">
              <img
                src={p4lLogo}
                alt="Psych For Life logo"
                className="w-32 h-32 mb-2"
              />
              <p className="text-gray-900 text-3xl">
                Psych For Life<sup>®</sup>
              </p>
            </div>

            {/* Initial/Loading State */}
            {(verificationState === "initial" ||
              verificationState === "loading") &&
              hasRequiredParams && (
                <div className="space-y-6">
                  <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 mb-4">
                      {getTitle()}
                    </h1>
                    <p className="text-gray-600 text-base leading-relaxed">
                      {getMessage()}
                    </p>
                  </div>

                  <Button
                    className="w-full py-4 text-lg"
                    onClick={handleConfirmEmail}
                    loading={verificationState === "loading"}
                    disabled={verificationState === "loading"}
                  >
                    {verificationState === "loading"
                      ? "Verifying..."
                      : "Confirm Email"}
                  </Button>
                </div>
              )}

            {/* Success State */}
            {isSuccessState && (
              <div className="space-y-6">
                <div className="flex flex-col items-center text-center">
                  {getIcon()}
                  <h2 className="text-2xl font-bold text-gray-900 mb-2 mt-4">
                    {getTitle()}
                  </h2>
                  <p className="text-gray-600 text-base leading-relaxed mb-6">
                    {getMessage()}
                  </p>
                </div>

                <div className="space-y-3">
                  {tutorialStatus?.needsTutorial ? (
                    <Button
                      className="w-full py-4 text-lg bg-green-600 hover:bg-green-700"
                      onClick={handleGoToTutorial}
                    >
                      Get Started with Tutorial
                    </Button>
                  ) : (
                    <Button
                      className="w-full py-4 text-white text-lg bg-green-600 hover:bg-green-700"
                      onClick={handleGoToLogin}
                    >
                      Sign In to Your Account
                    </Button>
                  )}

                  <Button
                    className="w-full py-3"
                    onClick={handleGoToHome}
                    variant="secondary"
                  >
                    Return to Home
                  </Button>
                </div>

                {/* Success Additional Info */}
                <div className="mt-8 pt-6 border-t border-gray-100">
                  <p className="text-sm text-gray-500 text-center">
                    {verificationState === "success" ? (
                      <>
                        Welcome to Psych for Life<sup>®</sup>! You can now
                        access all features of your account.
                      </>
                    ) : (
                      "Your account is active and ready to use."
                    )}
                  </p>
                </div>
              </div>
            )}

            {/* Error State */}
            {isErrorState && (
              <div className="space-y-6">
                <div className="flex flex-col items-center text-center">
                  {getIcon()}
                  <h2 className="text-2xl font-bold text-gray-900 mb-2 mt-4">
                    {getTitle()}
                  </h2>
                  <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                    <p className="text-red-700 text-sm">{getMessage()}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {/* Primary action based on error type */}
                  {verificationState === "expired" ? (
                    <Button
                      className="w-full py-3"
                      onClick={handleGoToLogin}
                      variant="primary"
                    >
                      Try Signing In
                    </Button>
                  ) : verificationState === "invalid" ||
                    verificationState === "not_found" ? (
                    <Button
                      className="w-full py-3"
                      onClick={handleGoToLogin}
                      variant="primary"
                    >
                      Go to Login
                    </Button>
                  ) : (
                    <Button
                      className="w-full py-3"
                      onClick={handleGoToLogin}
                      variant="primary"
                    >
                      Try Signing In
                    </Button>
                  )}

                  {/* Secondary actions */}
                  {(verificationState === "invalid" ||
                    verificationState === "expired") && (
                    <Button
                      className="w-full py-3"
                      onClick={handleGoToSignup}
                      variant="secondary"
                    >
                      Create New Account
                    </Button>
                  )}

                  <Button
                    className="w-full py-3"
                    onClick={handleGoToHome}
                    variant="alternative"
                  >
                    Return to Home
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmUserPage;
